/**
 * Frontend metadata for the Baseball Trend flow.
 *
 * Mirrors the public preset shape served by `/api/baseball-trend-presets`.
 * The actual image/video prompts live server-only in
 * `server/src/config/baseballTrend.ts`.
 *
 * Credit cost is NOT mirrored here. The UI reads the canonical
 * per-(model, variant) price from the `credit_costs` table via
 * `useCreditCosts()` — the exact source the server charges from
 * (see `src/pages/BaseballTrend/useBaseballTrend.ts`). No bundle, no markup.
 */

import type {
  BaseballTrendAspectRatio,
  BaseballTrendDuration,
  BaseballTrendImageModelId,
  BaseballTrendPresetId,
} from "@shared/schemas/api";

export type {
  BaseballTrendAspectRatio,
  BaseballTrendDuration,
  BaseballTrendImageModelId,
  BaseballTrendPresetId,
};

/** Image model used for the Stage-1 preview pair. GPT Image 2 is the only
 *  model surfaced for this flow — it gives the best broadcast-style look
 *  for the "caught on live TV" frame. No picker in the UI. */
export const BASEBALL_TREND_IMAGE_MODEL_IDS: ReadonlyArray<BaseballTrendImageModelId> = [
  "gpt-image-2",
];

export const BASEBALL_TREND_DEFAULT_IMAGE_MODEL_ID: BaseballTrendImageModelId =
  "gpt-image-2";

/** Resolution the Stage-1 preview image runs at — mirror of server
 *  `BASEBALL_TREND_IMAGE_RESOLUTION`. Used to build the `credit_costs`
 *  lookup key (`image_generate:<model>:1k`) the server charges from. */
export const BASEBALL_TREND_IMAGE_RESOLUTION = "1k" as const;

/** Stage-2 render model + resolution — mirror of the server's Stage-2 Kling
 *  call. Used to build the `credit_costs` video lookup key
 *  (`video_generate:kling-v3-0-standard:1080p_<dur>s`). No magic strings in
 *  the hook. */
export const BASEBALL_TREND_RENDER_MODEL_ID = "kling-v3-0-standard" as const;
export const BASEBALL_TREND_RENDER_RESOLUTION = "1080p" as const;

export interface BaseballTrendPreset {
  id: BaseballTrendPresetId;
  nameKey: string;
  /** R2 URL for the tile image in the preset grid. */
  previewImage: string;
}

/** Allowed durations — Kling 3.0 supports only 5s and 10s.
 *  Keep in sync with `BASEBALL_TREND_DURATIONS` on the server. */
export const BASEBALL_TREND_DURATIONS: BaseballTrendDuration[] = [5, 10];
export const BASEBALL_TREND_DEFAULT_DURATION: BaseballTrendDuration = 5;

/** Keep in sync with `BASEBALL_TREND_PREVIEW_COUNT` on the server. */
export const BASEBALL_TREND_PREVIEW_COUNT = 2;

/** Allowed output aspect ratios. Same value is sent to BOTH the Stage-1
 *  image and the Stage-2 video. */
export const BASEBALL_TREND_ASPECT_RATIOS: BaseballTrendAspectRatio[] = ["16:9", "9:16"];
export const BASEBALL_TREND_DEFAULT_ASPECT_RATIO: BaseballTrendAspectRatio = "16:9";

/** Example before/after for the hero showcase.
 *  - BEFORE: reuses the Profile-Photo flow's hero "before" image (same casual
 *    portrait, already on R2 — no duplicate upload needed).
 *  - AFTER: a real Kling 3.0 broadcast clip uploaded to R2 by
 *    `upload-static-presets.ts`. The `.mp4` suffix is the signal to the
 *    hero showcase component to render a `<video>` instead of `<img>`. */
const CDN_BASE = "https://cdn.todowith.ai/generated/2ae2c1db-53d0-4920-9cf4-a6b5a96db17b";
export const BASEBALL_TREND_EXAMPLE_BEFORE = `${CDN_BASE}/hero-profile-photo-before.webp`;
export const BASEBALL_TREND_EXAMPLE_AFTER = `${CDN_BASE}/hero-baseball-trend-after-v2.mp4`;
